import { Helmet } from "react-helmet";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useEffect, useState } from "react";

import PlusIcon from "./../../../assets/icons/plus.svg?react";
import MoreIcon from "./../../../assets/icons/more.svg?react";
// import ArrowRightIcon from "./../../../assets/icons/arrow-right.svg?react";
import ArchiveIcon from "./../../../assets/icons/archive-box.svg?react";
import RefreshIcon from "./../../../assets/icons/arrow-path.svg?react";
import PauseIcon from "./../../../assets/icons/puse-circle.svg?react";
import PlayIcon from "./../../../assets/icons/play.svg?react";
import { useNavigate } from "react-router-dom";
import {
  DataServiceConnectorEditUrl,
  DataServiceGeneralInformationUrl,
} from "../../../utils/urls";
import { DeleteModal } from "../../modals/QuestionModal";
import { useSearch } from "../../../providers/SearchProvider";

import {
  deleteDSSConnectorAsync,
  getDSSCapabilitiesConnectorsAsync,
  getDSSConnectorAsync,
  getDSSConnectorsAsync,
  getDSSConnectorTasksAsync,
  handleToggleDSSConnectorModal,
  pauseDSSConnectorAsync,
  restartDSSConnectorAsync,
  resumeDSSConnectorAsync,
  selectDatabaseService,
  selectDatabaseServiceLoading,
  selectDSSCapabilitiesConnectors,
  selectDSSConnectors,
  selectDSSConnectorsActionLoading,
  selectDSSConnectorsLoading,
} from "../../../store/database/serviceSlice";
import EditIcon from "./../../../assets/icons/pencil-square.svg?react";
import { generateServicePermissions } from "../../../pages/databases/ServicePage";
import {
  Button,
  Dropdown,
  EmptyState,
  SimpleTable,
  Tag,
  Typography,
} from "djuno-design";
import HighlighterText from "../../general/HighlighterText";
import { Connector } from "../../../types/data-straming";
import { ConnectorCreateModal } from "./ConnectorCreateModal";

const ConnectorsTab = () => {
  const [deleteConnector, setDeleteConnector] = useState<Connector | null>(
    null
  );

  const connectorsCapability = useAppSelector(selectDSSCapabilitiesConnectors);
  const connectors = useAppSelector(selectDSSConnectors);
  const connectorsLoading = useAppSelector(selectDSSConnectorsLoading);
  const [filteredConnectors, setFilteredConnectors] = useState<Connector[]>([]);

  const service = useAppSelector(selectDatabaseService);
  const loading = useAppSelector(selectDatabaseServiceLoading);

  const actionLoading = useAppSelector(selectDSSConnectorsActionLoading);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { value: searchValue } = useSearch();

  useEffect(() => {
    if (service) {
      const permissions = generateServicePermissions().connectors;
      if (!permissions.includes(service.engine)) {
        navigate(DataServiceGeneralInformationUrl(service.engine, service.id));
      } else {
        dispatch(
          getDSSConnectorsAsync({
            engine: service.engine,
            clusterId: service.id,
          })
        );
      }
    }
  }, [dispatch, navigate, searchValue, service]);

  useEffect(() => {
    const lookedUpFiles = connectors?.filter((r) =>
      r.name?.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredConnectors(lookedUpFiles);
  }, [dispatch, searchValue, connectors]);

  useEffect(() => {
    service &&
      dispatch(
        getDSSCapabilitiesConnectorsAsync({
          engine: service.engine,
          clusterId: service.id,
        })
      );
  }, [dispatch, service]);

  const handlePauseConnector = (connectorId: string) => {
    if (!service) return;

    dispatch(
      pauseDSSConnectorAsync({
        engine: service.engine,
        clusterId: service.id,
        connectorId: connectorId,
      })
    ).then((action) => {
      if (action.type === "service/connector/pause/fulfilled") {
        dispatch(
          getDSSConnectorAsync({
            engine: service.engine,
            clusterId: service.id,
            connectorId,
          })
        );
      }
    });
  };

  const handleRestartConnector = (connectorId: string) => {
    if (!service) return;

    dispatch(
      restartDSSConnectorAsync({
        engine: service.engine,
        clusterId: service.id,
        connectorId: connectorId,
      })
    ).then((action) => {
      if (action.type === "service/connector/restart/fulfilled") {
        dispatch(
          getDSSConnectorAsync({
            engine: service.engine,
            clusterId: service.id,
            connectorId,
          })
        );
      }
    });
  };
  const handleResumeConnector = (connectorId: string) => {
    if (!service) return;

    dispatch(
      resumeDSSConnectorAsync({
        engine: service.engine,
        clusterId: service.id,
        connectorId: connectorId,
      })
    ).then((action) => {
      if (action.type === "service/connector/resume/fulfilled") {
        dispatch(
          getDSSConnectorAsync({
            engine: service.engine,
            clusterId: service.id,
            connectorId,
          })
        );
      }
    });
  };

  useEffect(() => {
    if (!service || filteredConnectors.length === 0) return;

    const intervals = filteredConnectors.map((connector) =>
      setInterval(() => {
        dispatch(
          getDSSConnectorAsync({
            engine: service.engine,
            clusterId: service.id,
            connectorId: connector.id,
          })
        );
      }, 20000)
    );

    return () => {
      intervals.forEach(clearInterval);
    };
  }, [dispatch, service, filteredConnectors]);

  useEffect(() => {
    if (!service || filteredConnectors.length === 0) return;

    filteredConnectors.forEach((connector) => {
      dispatch(
        getDSSConnectorTasksAsync({
          engine: service.engine,
          clusterId: service.id,
          connectorId: connector.id,
        })
      );
    });
  }, [dispatch, service, filteredConnectors]);

  return (
    <>
      <Helmet>
        <title>{import.meta.env.VITE_APP_NAME} | Connectors</title>
        <meta name="description" content="" />
      </Helmet>

      {service && (
        <>
          <div className="flex items-center justify-between">
            <div className="items-center justify-between flex flex-1 transition duration-150">
              <Typography.Title level={5} className="!mb-0">
                Connectors
              </Typography.Title>
            </div>
            <div className="flex items-center gap-2">
              <Button
                uiType="light"
                onClick={() => {
                  if (service) {
                    dispatch(
                      getDSSConnectorsAsync({
                        engine: service.engine,
                        clusterId: service.id,
                      })
                    );
                  }
                }}
                disabled={!service}
                className="group"
                tooltip={{ content: "Refresh" }}
              >
                <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
              </Button>
              <Button
                uiType="primary"
                onClick={() => dispatch(handleToggleDSSConnectorModal(true))}
                className="group"
              >
                Add a connector
                <PlusIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
              </Button>
            </div>
          </div>

          <div className="mt-5 w-full">
            <SimpleTable
              loading={loading || connectorsLoading}
              containerClassName="min-h-[240px]"
            >
              <SimpleTable.Head>
                <SimpleTable.Row>
                  <SimpleTable.TH lable="Name" />
                  <SimpleTable.TH lable="Connector" />
                  <SimpleTable.TH lable="Status" />
                  <SimpleTable.TH lable="Tasks" />
                  <SimpleTable.TH lable="Status" />
                  <SimpleTable.TH lable="Restart" />
                  <SimpleTable.TH lable="" />
                </SimpleTable.Row>
              </SimpleTable.Head>
              <SimpleTable.Body>
                {service &&
                  filteredConnectors.map((connector, i) => (
                    <SimpleTable.Row key={i}>
                      <SimpleTable.TD>
                        <div className="flex items-center flex-row justify-between space-x-3">
                          <HighlighterText
                            searchValue={searchValue}
                            textToHighlight={connector.name}
                            className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                          />
                        </div>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {
                            connectorsCapability.find(
                              (c) => c.id === connector.connectorId
                            )?.name
                          }
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Tag
                          className="text-xs"
                          color={
                            connector.status === "FAILED"
                              ? "warning"
                              : connector.status === "PAUSED"
                              ? "error"
                              : "success"
                          }
                        >
                          {connector.status}
                        </Tag>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          0/0
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        {connector.status === "PAUSED" ? (
                          <Button
                            onClick={() => handleResumeConnector(connector.id)}
                            // disabled={connectorLoading}
                            uiType="icon"
                          >
                            <PlayIcon className="w-4" />
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handlePauseConnector(connector.id)}
                            uiType="icon"
                            // disabled={connectorLoading}
                          >
                            <PauseIcon className="w-4" />
                          </Button>
                        )}
                      </SimpleTable.TD>

                      <SimpleTable.TD>
                        <Button
                          onClick={() => handleRestartConnector(connector.id)}
                          uiType="icon"
                          // disabled={connectorLoading}
                        >
                          <RefreshIcon className="w-4" />
                        </Button>
                      </SimpleTable.TD>
                      <SimpleTable.TD className="w-20 gap-1">
                        <div className="h-full w-full inline-flex items-center justify-end gap-1">
                          <div className="w-8 flex justify-center items-center">
                            <Dropdown
                              itemsClassName="!w-48"
                              anchor="bottom end"
                              menu={[
                                {
                                  key: "1",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <EditIcon className="w-4"></EditIcon>
                                      Edit
                                    </div>
                                  ),
                                  onClick: (_, close) => {
                                    close();
                                    navigate(
                                      DataServiceConnectorEditUrl(
                                        service.engine,
                                        service.id,
                                        connector.id
                                      )
                                    );
                                  },
                                  disabled: loading,
                                },

                                {
                                  type: "divider",
                                },
                                {
                                  key: "end",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <ArchiveIcon className="w-4" />
                                      Delete
                                    </div>
                                  ),
                                  danger: true,
                                  onClick: (_, close) => {
                                    close();
                                    setDeleteConnector(connector);
                                  },
                                  disabled: loading,
                                },
                              ]}
                            >
                              <Button
                                uiType="icon"
                                uiSize="small"
                                className="!px-2"
                              >
                                <MoreIcon className="w-4 h-4" />
                              </Button>
                            </Dropdown>
                          </div>
                          {/* <ArrowRightIcon className="w-4 h-4 dark:text-slate-100 text-slate-800 cursor-pointer" /> */}
                        </div>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  ))}

                {connectors.length === 0 && (
                  <SimpleTable.Row withoutHoverStyle className="h-[200px]">
                    <SimpleTable.TD colSpan={7} className="!border-0">
                      <EmptyState text="You have not created any Connector Flow yet" />
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                )}
              </SimpleTable.Body>
            </SimpleTable>
          </div>
          <ConnectorCreateModal />

          <DeleteModal
            title="Delete Connector Flow"
            isOpen={deleteConnector !== null}
            onClose={() => setDeleteConnector(null)}
            description=""
            confirmButtonText="Confirm"
            confirmButtonType="danger"
            confirmButtonClassName="w-full"
            onConfirm={async () => {
              if (!deleteConnector || !service) {
                setDeleteConnector(null);
                return;
              }

              const action = await dispatch(
                deleteDSSConnectorAsync({
                  engine: service.engine,
                  clusterId: service.id,
                  connectorId: deleteConnector.id,
                })
              );

              if (action.type === "service/connectors/delete/fulfilled") {
                await dispatch(
                  getDSSConnectorsAsync({
                    engine: service.engine,
                    clusterId: service.id,
                  })
                );
              }

              setDeleteConnector(null);
            }}
            loading={actionLoading}
          />
        </>
      )}
    </>
  );
};

export default ConnectorsTab;
